import dayjs, { type Dayjs } from 'dayjs'
import type { TimePeriod } from './types'

export interface EventDateWindowArgs {
  dateRange: [Dayjs, Dayjs] | null
  timePeriod: TimePeriod
}

export interface EventDateWindow {
  startsAfter: string | null
  startsBefore: string | null
}

/**
 * The date window events are filtered to, derived from the same filter state
 * the games query uses.
 *
 * Mirrors `MyScheduleContext`'s own `getDateRange`, which is provider-internal
 * and not exposed on the context value. Kept in sync deliberately rather than
 * widening the context's public surface for one consumer: a custom range wins
 * over the time period, UPCOMING includes today, and PAST is open-ended at the
 * start.
 *
 * Both bounds are widened to whole days so an event later in the day on the
 * final day of a range is not dropped.
 */
export const resolveEventDateWindow = ({
  dateRange,
  timePeriod,
}: EventDateWindowArgs): EventDateWindow => {
  if (dateRange) {
    return {
      startsAfter: dateRange[0].startOf('day').format(),
      startsBefore: dateRange[1].endOf('day').format(),
    }
  }

  switch (timePeriod) {
    case 'TODAY':
      return {
        startsAfter: dayjs().startOf('day').format(),
        startsBefore: dayjs().startOf('day').add(1, 'day').format(),
      }
    case 'PAST':
      return {
        startsAfter: null,
        startsBefore: dayjs().startOf('day').format(),
      }
    case 'UPCOMING':
      return {
        startsAfter: dayjs().startOf('day').format(),
        startsBefore: dayjs().startOf('day').add(180, 'day').format(),
      }
    default:
      // Same fallback as the provider's `getDateRange`: a today-shaped window.
      return {
        startsAfter: dayjs().startOf('day').format(),
        startsBefore: dayjs().startOf('day').add(1, 'day').format(),
      }
  }
}
